import { t } from '@shared/helpers/translation';
import { ProductType, FlightRefund, FlightTicket, FlightType } from './myBooking.interfaces';
import { FlightTicketStatus } from '@/store/myBooking/types';

interface FlightStatusState {
    type: FlightType;
    number?: string | null;
}

export function isMainProduct(type: ProductType) {
    const mainTypes = [
        ProductType.Hotel,
        ProductType.Flight,
        ProductType.Ferry,
        ProductType.Activity,
        ProductType.Golf,
        ProductType.Transfer,
    ];

    return mainTypes.includes(type);
}

export function getFlightStatus(
    ticket: FlightTicket,
    refund: FlightRefund | null
): FlightTicketStatus | null {
    const { airlineNumber, type } = ticket;
    const number = airlineNumber && airlineNumber !== '' ? airlineNumber : null;

    if (ticket.type === FlightType.Ryanair && !ticket?.verification?.confirmed) {
        return null;
    }

    if (refund && refund.pending) {
        return {
            title: t({ key: 'shared.refund_in_progress' }),
            productText: t({ key: 'shared.refund_in_progress' }),
            description: t({ key: 'important.awaiting_refund_from_airline' }),
            color: 'yellow',
            refund: true,
        };
    }

    const state: FlightStatusState = {
        type,
        number,
    };

    const title = getTicketTitle(state);
    const productText = getProductText(state);
    const description = getDescription(state);
    const color = getColor(state);

    return {
        title,
        productText,
        description,
        color,
        refund: false,
    };
}

function getTicketTitle({ type, number }: FlightStatusState) {
    switch (type) {
        case FlightType.LCC:
            return t({ key: 'shared.confirmed_by_airline' });
        default:
            return number ?? t({ key: 'shared.confirmed' });
    }
}

function getProductText({ type }: FlightStatusState) {
    switch (type) {
        case FlightType.LCC:
            return t({ key: 'shared.confirmed_by_airline' });
        default:
            return t({ key: 'shared.confirmed' });
    }
}

const getColor = ({ type }: FlightStatusState) => {
    switch (type) {
        case FlightType.LCC:
            return 'yellow';
        default:
            return 'green';
    }
};

const getDescription = ({ type, number }: FlightStatusState) => {
    switch (type) {
        case FlightType.LCC:
            return t({ key: 'important.tickets_on_request_lcc' });
        case FlightType.Other:
        case FlightType.TravelFusion:
            return !number
                ? t({ key: 'important.confirmed_status_info' })
                : t({ key: 'important.check_in_with_booking_number' });
        case FlightType.Ryanair:
            return !number
                ? t({ key: 'important.confirmed_status_info' })
                : t({ key: 'important.check_in_ryanair_with_booking_number' });
        default:
            return null;
    }
};
